import React, { FC } from 'react';
import { CentralColumnProps } from '@components/ncFileVersions/types';
import { scale } from '@components/frame';
import { getBoxShadowStyle } from '@styles/shadow';
import { isPhone, isPhoneNative, isTabletOrWeb, isWeb } from '@helpers';
import { ScrollView, Text, View, useWindowDimensions } from 'react-native';
import i18n from 'i18n-js';
import FileInfoBlock from './FileInfoBlock';
import { font } from '@styles/fonts';
import { PADDING_HORIZONTAL_FOR_SHADOW, SCREEN_BORDER_RADIUS, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import InputLine from '@components/machines/components/detailsComponents/activeMachine/components/InputLine';
import ActionButton from './ActionButton';
import { BOX_SHADOW_TYPE, LEFT_CENTRAL_BLOCK_WIDTH } from '@components/ncFileVersions/constants';
import Svg from '@styles/icons/svg';
import CompareCancelButton from './CompareCancelButton';
import CompareVersionsBlock from './CompareVersionsBlock';

const CentralColumn: FC<CentralColumnProps> = ({
  C,
  selectedVersion,
  ncFile,
  visible,
  twoColumnView,
  onSendToMachine,
  onMarkThisVersionAsInactive,
  comparisonMode = false,
  onEnterComparisonMode,
  hasMultipleVersions = false,
  versionsToCompare = [],
  onExitComparisonMode,
  onStartComparison,
}) => {
  const { width: viewportWidth = 0 } = useWindowDimensions();
  const hideCompareOnSmallViewport = isWeb && viewportWidth > 0 && viewportWidth <= 600;
  if (!visible) return null;

  const shadowStyles = getBoxShadowStyle({ type: BOX_SHADOW_TYPE, C });
  const shouldShowCompareCard =
    hasMultipleVersions && !comparisonMode && !!onEnterComparisonMode && !hideCompareOnSmallViewport;
  const shouldShowComparisonPrompt = comparisonMode && isTabletOrWeb && !hideCompareOnSmallViewport;

  const getSequenceValue = (version?: CentralColumnProps['selectedVersion']) => {
    if (version == null) {
      return Number.NEGATIVE_INFINITY;
    }
    const rawSequence = (version as any)?.sequenceNumber;
    if (typeof rawSequence === 'number') {
      return rawSequence;
    }
    const parsedSequence = Number(rawSequence);
    return Number.isFinite(parsedSequence) ? parsedSequence : Number.NEGATIVE_INFINITY;
  };
  const latestVersionForPill =
    comparisonMode && versionsToCompare?.length
      ? versionsToCompare.reduce<CentralColumnProps['selectedVersion'] | undefined>((latest, current) => {
          if (!latest) {
            return current;
          }
          return getSequenceValue(current) > getSequenceValue(latest) ? current : latest;
        }, undefined) ?? selectedVersion
      : selectedVersion;

  const renderComparisonPrompt = () => {
    const canShowDiff = versionsToCompare?.length === 2;
    const comparisonTarget =
      versionsToCompare?.find((version) => version.ncFileVersionId !== selectedVersion?.ncFileVersionId) ??
      versionsToCompare?.[versionsToCompare.length - 1];
    const comparisonPromptText = canShowDiff
      ? i18n.t('Click Show to compare version {{sequenceNumber}}', { sequenceNumber: comparisonTarget?.sequenceNumber })
      : i18n.t('Select another version to compare');

    return (
      <View
        style={[!twoColumnView && isTabletOrWeb ? { width: scale(LEFT_CENTRAL_BLOCK_WIDTH) } : { flex: 1, width: '100%' }, { gap: scale(20) }]}
      >
        <View
          style={[
            shadowStyles,
            {
              borderRadius: scale(SCREEN_BORDER_RADIUS),
              backgroundColor: C('Background/Light/Field'),
            },
          ]}
        >
          <View
            style={{
              width: '100%',
              borderRadius: scale(SCREEN_BORDER_RADIUS),
              paddingVertical: scale(10),
              paddingHorizontal: scale(15),
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: scale(8),
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(8) }}>
              <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}>
                {i18n.t('Version')} {latestVersionForPill?.sequenceNumber}
              </Text>
              {latestVersionForPill?.isFavorite && <Svg icon="starSelected" width={scale(18)} height={scale(18)} color="Label/Light/Primary" />}
            </View>
          </View>
        </View>
        <View style={{ gap: scale(16), width: '100%' }}>
          <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}>{comparisonPromptText}</Text>
          <CompareCancelButton testID={TEST_IDS.ncFileVersions.compareStartButton} C={C} variant="primary" disabled={!canShowDiff} onPress={canShowDiff ? onStartComparison : undefined} />
          <CompareCancelButton testID={TEST_IDS.ncFileVersions.compareModeCancelButton} C={C} variant="secondary" onPress={onExitComparisonMode} />
        </View>
      </View>
    );
  };

  // Phone-only: version header config for InputLine
  const centralColumnConfigTopPart = [
    {
      borderBottomRadius: false,
      borderTopRadius: true,
      onlyLabel: false,
      isText: false,
      data: [],
      index: 0,
      i: {
        Component: () => {
          return (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(8) }}>
              <Text style={[font.Headline, { color: C('Label/Light/Primary') }]}>
                {i18n.t('Version')} {selectedVersion?.sequenceNumber}
              </Text>
              {selectedVersion?.isFavorite && <Svg icon="starSelected" width={scale(18)} height={scale(18)} color="Label/Light/Primary" />}
            </View>
          );
        },
      },
      C,
      loading: !ncFile,
    },
  ];

  if (shouldShowComparisonPrompt && isTabletOrWeb) {
    return renderComparisonPrompt();
  }

  return (
    <View
      style={[!twoColumnView && isTabletOrWeb ? { width: scale(LEFT_CENTRAL_BLOCK_WIDTH) } : { flex: 1, width: '100%' }, { height: '100%' }]}
    >
      <ScrollView
        style={{ flex: 1, marginTop: -scale(PADDING_HORIZONTAL_FOR_SHADOW) }}
        contentContainerStyle={{
          gap: scale(20),
          paddingBottom: scale(20),
          paddingTop: scale(PADDING_HORIZONTAL_FOR_SHADOW),
          paddingHorizontal: scale(PADDING_HORIZONTAL_FOR_SHADOW),
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Phone only: version header + Send to Machine */}
        {isPhoneNative && (
          <View style={[shadowStyles, { borderRadius: scale(SCREEN_BORDER_RADIUS) }]}>
            {centralColumnConfigTopPart.map((c, i) => (
              <InputLine key={i} {...c} withoutStretchingForContainer />
            ))}
            <ActionButton
              {...{
                C,
                loading: false,
                text: `${i18n.t('Send To Machine')}...`,
                borderTopRadius: false,
                borderBottomRadius: true,
                action: onSendToMachine,
              }}
            />
          </View>
        )}

        {!isPhone && shouldShowCompareCard && onEnterComparisonMode && (
          <CompareVersionsBlock C={C} selectedVersion={selectedVersion} onEnterComparisonMode={onEnterComparisonMode} />
        )}

        {/* Phone only: file info block (Runs, First Cycle, First Cycle On, Last Cycle, Last Cycle On) */}
        {isPhoneNative && <FileInfoBlock C={C} selectedVersion={selectedVersion} ncFile={ncFile} />}

        {/* Phone only: Mark This Version Inactive */}
        {isPhoneNative && (
          <View style={[shadowStyles, { borderRadius: scale(SCREEN_BORDER_RADIUS) }]}>
            <ActionButton
              {...{
                action: onMarkThisVersionAsInactive,
                borderBottomRadius: true,
                borderTopRadius: true,
                C,
                loading: false,
                text: i18n.t('Mark This Version Inactive'),
              }}
            />
          </View>
        )}
      </ScrollView>
    </View>
  );
};

export default CentralColumn;
